import { useCallback, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { Tabs } from "@/features/power_projects/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import { ControlTab } from "../components/ControlTab";
import { FilesTab } from "../components/FilesTab";
import { LedJournalPanel, LedMapPanel, LedProjectHeader, ledTabItems, type LedTabKey } from "../components/LedSections";
import { MemosTab } from "../components/MemosTab";
import { ParcelDrawer } from "../components/ParcelDrawer";
import { ParcelsTable } from "../components/ParcelsTable";
import { RegisterTab } from "../components/RegisterTab";
import { ReportsTab } from "../components/ReportsTab";
import { TilesTab } from "../components/TilesTab";
import { ErrorLine } from "../components/common";
import { LedPreviewProvider, useLedJournal, useLedParcels, useLedSummary } from "../hooks";
import type { LedParcel } from "../types";

/** Zakładki widoku firmy → zakładki warsztatu ZE (tam ten sam układ nosi przedrostek `led_`,
 *  bo „Działki" i „Raporty" istnieją już jako karty DLC 01). */
const WORKSPACE_TAB: Record<LedTabKey, string> = {
  map: "map",
  files: "files",
  parcels: "led_parcels",
  register: "led_register",
  tiles: "led_tiles",
  memos: "led_memos",
  reports: "led_reports",
  journal: "led_journal",
  control: "led_control",
};

/** Karta projektu prowadzonego przez podwykonawcę — ekran FIRMY (/contractor/projects/:id).
 *  Pracownik GK, który tu trafi (link z portfela, ze zlecenia, z analizy), jest odsyłany do
 *  warsztatu ZE tego projektu: tam ma ten sam nagłówek i zakładki nadzoru, a pod nimi
 *  pełne karty warsztatu (Sprawy, Działki…). Jeden ekran po naszej stronie, nie dwa. */
export function LedProjectPage() {
  const [preview, setPreview] = useState(false);
  return (
    <LedPreviewProvider value={preview}>
      <LedProjectBody preview={preview} onPreview={setPreview} />
    </LedPreviewProvider>
  );
}

function LedProjectBody({ preview, onPreview }: { preview: boolean; onPreview: (v: boolean) => void }) {
  const { leadershipId } = useParams<{ leadershipId: string }>();
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ((searchParams.get("tab") as LedTabKey) || "map") as LedTabKey;
  const [days, setDays] = useState(7);
  const [openParcel, setOpenParcel] = useState<string | null>(searchParams.get("parcel"));
  const { data, isLoading, isError, error } = useLedSummary(leadershipId);
  const internal = !!data?.internal;
  const summary = data?.summary;
  const parcelsQuery = useLedParcels(leadershipId);
  const journalQuery = useLedJournal(leadershipId, days);
  usePageTitle(summary ? `${summary.name} — nadzór` : undefined);
  const canDecide = !!user && (user.is_ceo || (user.role === "director" && !!user.is_technical_director));
  const isContractor = user?.role === "contractor";
  const parcels = useMemo(() => parcelsQuery.data?.parcels ?? [], [parcelsQuery.data]);
  const onSelectParcel = useCallback((p: LedParcel) => setOpenParcel(p.id), []);
  const closeDrawer = useCallback(() => setOpenParcel(null), []);

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Spinner /></div>;
  if (isError || !summary) {
    return (
      <div className="p-6">
        <ErrorLine error={error ?? true} fallback="Nie udało się wczytać projektu — odśwież stronę." />
      </div>
    );
  }
  if (!isContractor) {
    const next = new URLSearchParams();
    next.set("tab", WORKSPACE_TAB[tab] ?? "map");
    if (openParcel) next.set("parcel", openParcel);
    return <Navigate to={`/power-projects/${summary.project_id}?${next.toString()}`} replace />;
  }

  const tutorialHref = `/contractor/projects/${summary.id}/route-tutorial`;
  const setTab = (k: LedTabKey) => setSearchParams((p) => { const n = new URLSearchParams(p); n.set("tab", k); return n; });
  const tabs = ledTabItems(summary.badges, journalQuery.data?.recent, internal && !preview);

  return (
    <div>
      <LedProjectHeader
        summary={summary}
        internal={internal}
        preview={preview}
        onPreview={onPreview}
        isContractor={isContractor}
        onOpenParcel={setOpenParcel}
        backTo="/contractor/projects"
        backLabel="Prowadzone projekty"
      />

      <Tabs<LedTabKey> tabs={tabs} active={tab} onChange={setTab} sticky />

      {tab === "map" && (
        <LedMapPanel
          summary={summary}
          parcelsData={parcelsQuery.data}
          parcelsLoading={parcelsQuery.isLoading}
          tutorialHref={tutorialHref}
          onSelectParcel={onSelectParcel}
        />
      )}
      {tab === "parcels" && <ParcelsTable parcels={parcels} projectName={summary.short_name || summary.name} onOpen={setOpenParcel} />}
      {tab === "register" && <RegisterTab leadershipId={summary.id} internal={internal} parcels={parcels} onOpenParcel={setOpenParcel} />}
      {tab === "tiles" && <TilesTab leadershipId={summary.id} internal={internal} contractorName={summary.contractor_name} />}
      {tab === "files" && <FilesTab leadershipId={summary.id} />}
      {tab === "memos" && <MemosTab leadershipId={summary.id} internal={internal} contractorName={summary.contractor_name} parcels={parcels} />}
      {tab === "reports" && <ReportsTab leadershipId={summary.id} internal={internal} />}
      {tab === "control" && internal && (
        <ControlTab summary={summary} canDecide={canDecide} />
      )}
      {tab === "journal" && (
        <LedJournalPanel
          summary={summary}
          internal={internal}
          days={days}
          onDaysChange={setDays}
          journal={journalQuery.data}
          loading={journalQuery.isLoading}
        />
      )}

      <ParcelDrawer leadershipId={summary.id} parcelId={openParcel} internal={internal} contractorName={summary.contractor_name} onClose={closeDrawer} />
    </div>
  );
}
